import type { PreventionListMeasureDTO } from '@ui/shared/util-api';
import { ListItem } from '@ui/shared/util-list';
import type { Provider } from './provider';
import { mapProviderFromDTO } from './provider';
import type { Venue } from './venue';
import { determineVenueType } from './venue';
import type { MeasureKind } from './measure-kind';
import { determineMeasureKindType } from './measure-kind';
import { GenderSelection } from './gender';

export class PreventionListItemModel extends ListItem {

    public readonly shortDescription: string;
    public readonly imagePath?: string;

    public readonly goals: string;
    public readonly intendedChanges?: Array<string>;
    public readonly categories: Array<string>;

    public readonly provider: Provider;

    public readonly groupSize?: string | null;
    public readonly humanGroupSize?: string | null;
    public readonly ageGroups: Array<string>;
    public readonly genderIcon?: string | null;
    public readonly audienceGender?: GenderSelection | null;
    public readonly accessibilityFeatures?: string | null;

    public readonly content: string;
    public readonly methods: string;
    public readonly needsTeacherInvolvement: boolean;
    public readonly hasFurtherOffers: boolean;
    public readonly furtherOffers?: string | null;

    public readonly timeframe?: string | null;

    public readonly venues?: Array<Venue>;
    public readonly measureKind: Array<MeasureKind>;
    public readonly website?: string | null;

    public readonly cost?: string | null;

    // additional_notes?: string | null;

    constructor(mapItem: PreventionListMeasureDTO) {
        super(mapItem);

        this.shortDescription = mapItem.short_description;
        this.imagePath = mapItem.image?.image;
        this.goals = mapItem.goals;
        this.intendedChanges = mapItem.intended_changes?.map(({ name }) => name);
        this.categories = mapItem.categories.map(category => category.name);

        this.provider = mapProviderFromDTO(mapItem);

        this.ageGroups = mapItem.age_groups.map(group => group.name);
        this.accessibilityFeatures = mapItem.accessibility_features;
        this.content = mapItem.content;
        this.methods = mapItem.methods;
        this.needsTeacherInvolvement = mapItem.needs_teacher_involvement;
        this.hasFurtherOffers = mapItem.has_further_offers;
        this.furtherOffers = mapItem.further_offers;

        this.timeframe = mapItem.timeframe;

        this.venues = mapItem.venues?.map(determineVenueType);
        this.measureKind = mapItem.measure_kind?.map(determineMeasureKindType) ?? [];
        this.website = mapItem.website;

        this.cost = mapItem.cost;

        const min = mapItem.group_size_min ?? null;
        const max = mapItem.group_size_max ?? null;

        let humanGroupSize: string | null = null;

        if (min != null && max != null) {
            humanGroupSize = `${ min.toLocaleString() } – ${ max.toLocaleString() } Personen`;
        } else if (min != null) {
            humanGroupSize = `ab ${ min.toLocaleString() } Personen`;
        } else if (max != null) {
            humanGroupSize = `bis ${ max.toLocaleString() } Personen`;
        }
        this.humanGroupSize = humanGroupSize;

        this.groupSize = mapItem.group_size;

        this.audienceGender = mapItem.audience_gender === 'MALE'
            ? GenderSelection.Male
            : mapItem.audience_gender === 'FEMALE'
                ? GenderSelection.Female
                : GenderSelection.Undefined;
    }
}
